<template>
  <CCard>
    <CCardHeader class="d-flex justify-content-end align-items-center">
      <h2 class="text-uppercase font-weight-bold mb-0 mr-auto">Devices</h2>
      <CInput
        v-model="filters.search"
        class="mb-0"
        placeholder="Search"
        @update:value="fetchData"
        @focusout.stop
      />
    </CCardHeader>

    <CCardBody>
      <CDataTable
        :items="data"
        :fields="fields"
        :pagination="false"
        :sorter="{ external: true }"
        :loading="loading"
        class="mb-0"
        @update:sorter-value="sortingChange"
      >
        <template #user="{ item }">
          <td>
            <div v-if="item.user">
              <strong>{{ item.user.name }}</strong>
              <br />
              <small class="text-muted">{{ item.user.email }}</small>
            </div>
            <span v-else class="text-muted font-italic">Unassigned</span>
          </td>
        </template>

        <template #active="{ item }">
          <td class="text-center">
            <span
              :class="item.active ? 'badge bg-success text-white' : 'badge bg-secondary text-white'"
            >
              {{ item.active ? 'Yes' : 'No' }}
            </span>
            <div
              v-if="item.has_pending_deactivate"
              class="text-warning small mt-1 font-weight-bold"
            >
              <i class="fas fa-clock" />
              Pending Deactivation
            </div>
            <div v-if="item.has_pending_delete" class="text-danger small mt-1 font-weight-bold">
              <i class="fas fa-trash-alt" />
              Pending Delete
            </div>
          </td>
        </template>

        <template #status="{ item }">
          <td>
            <span v-if="item.in_tablet" class="badge bg-success text-white mr-2 p-2">
              Registered
            </span>
            <span v-if="item.in_history" class="badge bg-warning text-dark p-2">Grace Period</span>
          </td>
        </template>

        <template #actions="{ item }">
          <td>
            <Spinner v-if="loadingTablets[item.id || item.device_id]" :small="true" />
            <template v-else>
              <CButton
                v-if="!item.active"
                v-c-tooltip="'Activate Tablet'"
                size="sm"
                color="success"
                class="text-white mr-2"
                @click="activateTablet(item.id)"
              >
                <i class="fas fa-play" />
              </CButton>

              <CButton
                v-if="item.active"
                v-c-tooltip="'Deactivate Tablet'"
                size="sm"
                color="secondary"
                class="text-white mr-2"
                @click="deactivateTablet(item.id)"
              >
                <i class="fas fa-pause" />
              </CButton>

              <CButton
                v-c-tooltip="'Assign / Transfer to User'"
                size="sm"
                color="info"
                class="text-white mr-2"
                @click="openUserSelectModal(item)"
              >
                <i class="fas fa-exchange-alt" />
              </CButton>

              <CButton
                v-if="item.user"
                v-c-tooltip="'Unassign User'"
                size="sm"
                color="secondary"
                class="text-white mr-2"
                @click="askUnassign(item.id)"
              >
                <i class="fas fa-user-slash" />
              </CButton>

              <CButton
                v-if="item.has_pending_delete || item.has_pending_deactivate"
                v-c-tooltip="'Apply Request'"
                size="sm"
                color="primary"
                class="text-white mr-2"
                @click="applyPending(item.id)"
              >
                <i class="fas fa-check" />
              </CButton>

              <CButton
                v-if="item.has_pending_delete || item.has_pending_deactivate"
                v-c-tooltip="'Deny Request'"
                size="sm"
                color="warning"
                class="text-white mr-2"
                @click="denyPending(item.id)"
              >
                <i class="fas fa-times" />
              </CButton>

              <CButton
                v-c-tooltip="'History'"
                size="sm"
                color="dark"
                class="text-white mr-2"
                @click="viewHistory(item.id)"
              >
                <i class="fas fa-history" />
              </CButton>

              <CButton
                v-c-tooltip="'Delete Tablet'"
                size="sm"
                color="danger"
                class="text-white mr-2"
                @click="askDestroyTablet(item.id)"
              >
                <i class="fas fa-trash-alt" />
              </CButton>

              <CButton
                v-if="item.in_history"
                v-c-tooltip="'Delete Grace Period History'"
                size="sm"
                color="warning"
                class="text-white"
                @click="askDeleteGrace(item.device_id)"
              >
                <i class="far fa-eraser" />
              </CButton>
            </template>
          </td>
        </template>
      </CDataTable>

      <pagination
        :current-page="options.page"
        :last-page="options.last_page"
        class="mr-3"
        @paginate="pageChange"
      />
    </CCardBody>

    <EnterpriseUserSelectModal ref="userSelectModal" @selected="handleUserSelected" />
    <confirm-action-modal ref="confirm_modal" @confirmed="handleConfirmedAction" />
    <TabletHistoryModal ref="historyModal" />
  </CCard>
</template>

<script>
import externalTable from '@/mixins/externalTable';
import EnterpriseUserSelectModal from '@/components/EnterpriseUserSelectModal.vue';
import Spinner from '@/components/Spinner.vue';
import ConfirmActionModal from '@/components/ConfirmActionModal.vue';
import TabletHistoryModal from '@/components/TabletHistoryModal.vue';

export default {
  name: 'TabletDevicesList',
  components: {
    EnterpriseUserSelectModal,
    Spinner,
    ConfirmActionModal,
    TabletHistoryModal,
  },
  mixins: [externalTable],
  data() {
    return {
      filters: {
        search: '',
      },
      fields: [
        { key: 'device_name', label: 'Device Name' },
        { key: 'device_id', label: 'Device ID' },
        { key: 'user', label: 'Assigned User', sorter: false },
        { key: 'active', label: 'Active' },
        { key: 'status', label: 'Status', sorter: false },
        { key: 'actions', label: 'Actions', sorter: false },
      ],
      url: '/enterprise-tablets',
      activeTabletForAssignment: null,
      loadingTablets: {},
    };
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    sortingChange(val) {
      const sortMap = {
        device_name: 'name',
      };
      val.column = sortMap[val.column] || val.column;
      this.options.order_by = val.column;
      this.options.order = val.asc ? 'asc' : 'desc';
      this.fetchData();
    },
    activateTablet(id) {
      this.addLoadingTablet(id);
      this.$http
        .post(`${this.url}/${id}/activate`)
        .then(({ data }) => {
          this.$toast.success(data.message || 'Tablet activated!');
          this.fetchData();
        })
        .catch(({ response }) => {
          this.$toast.error(response?.data?.message || 'Error activating tablet');
        })
        .finally(() => {
          this.removeLoadingTablet(id);
        });
    },

    deactivateTablet(id) {
      this.addLoadingTablet(id);
      this.$http
        .post(`${this.url}/${id}/deactivate`)
        .then(({ data }) => {
          this.$toast.success(data.message || 'Tablet deactivated!');
          this.fetchData();
        })
        .catch(({ response }) => {
          this.$toast.error(response?.data?.message || 'Error deactivating tablet');
        })
        .finally(() => {
          this.removeLoadingTablet(id);
        });
    },

    applyPending(id) {
      this.addLoadingTablet(id);
      this.$http
        .post(`${this.url}/${id}/apply-pending`)
        .then(() => {
          this.$toast.success('Pending request applied!');
          this.fetchData();
        })
        .catch(({ response }) => {
          this.$toast.error(response?.data?.message || 'Error applying request');
        })
        .finally(() => {
          this.removeLoadingTablet(id);
        });
    },

    denyPending(id) {
      this.addLoadingTablet(id);
      this.$http
        .post(`${this.url}/${id}/deny-pending`)
        .then(() => {
          this.$toast.success('Pending request denied!');
          this.fetchData();
        })
        .catch(({ response }) => {
          this.$toast.error(response?.data?.message || 'Error denying request');
        })
        .finally(() => {
          this.removeLoadingTablet(id);
        });
    },

    openUserSelectModal(tablet) {
      this.activeTabletForAssignment = tablet;
      this.$refs.userSelectModal.open();
    },

    handleUserSelected(user) {
      if (!this.activeTabletForAssignment) return;

      const tabletId = this.activeTabletForAssignment.id;
      const userId = user.id;
      this.addLoadingTablet(tabletId);
      this.$http
        .post(`${this.url}/${tabletId}/assign`, { user_id: userId })
        .then(({ data }) => {
          this.$toast.success(data.message || 'Tablet assigned successfully!');
          this.fetchData();
        })
        .catch(({ response }) => {
          this.$toast.error(response?.data?.message || 'Error assigning tablet');
        })
        .finally(() => {
          this.activeTabletForAssignment = null;
          this.removeLoadingTablet(tabletId);
        });
    },

    askDestroyTablet(id) {
      this.$refs.confirm_modal.open(
        { action: 'destroy_tablet', id: id },
        `Are you sure you want to delete this tablet?`,
      );
    },

    askUnassign(id) {
      this.$refs.confirm_modal.open(
        { action: 'unassign', id: id },
        `Are you sure you want to unassign this tablet?`,
      );
    },

    askDeleteGrace(deviceId) {
      this.$refs.confirm_modal.open(
        { action: 'delete_grace', deviceId: deviceId },
        `Are you sure you want to delete the grace period history for ${deviceId}?`,
      );
    },

    askDeleteTablet(deviceId) {
      this.$refs.confirm_modal.open(
        { action: 'delete_tablet', deviceId: deviceId },
        `Are you sure you want to permanently delete the tablet record for ${deviceId}?`,
      );
    },

    handleConfirmedAction(payload) {
      const id = payload.id || payload.deviceId;
      this.addLoadingTablet(id);

      if (payload.action === 'destroy_tablet') {
        this.destroyTablet(payload.id);
      } else if (payload.action === 'delete_grace') {
        this.deleteGrace(payload.deviceId);
      } else if (payload.action === 'delete_tablet') {
        this.deleteTablet(payload.deviceId);
      } else if (payload.action === 'unassign') {
        this.unassignTablet(payload.id);
      }
    },

    destroyTablet(id) {
      this.$http
        .delete(`${this.url}/${id}`)
        .then(({ data }) => {
          this.$toast.success(data.message || 'Deleted!');
          this.fetchData();
        })
        .catch(({ response }) => {
          this.$toast.error(response?.data?.message || 'Error deleting tablet');
        })
        .finally(() => {
          this.removeLoadingTablet(id);
        });
    },

    unassignTablet(id) {
      this.$http
        .post(`${this.url}/${id}/unassign`)
        .then(({ data }) => {
          this.$toast.success(data.message || 'Tablet unassigned!');
          this.fetchData();
        })
        .catch(({ response }) => {
          this.$toast.error(response?.data?.message || 'Error unassigning tablet');
        })
        .finally(() => {
          this.removeLoadingTablet(id);
        });
    },

    deleteGrace(deviceId) {
      this.$http
        .delete(`devices/${deviceId}/grace`)
        .then(({ data }) => {
          this.$toast.success(data.message || 'Grace period history deleted!');
          this.fetchData();
        })
        .catch(({ response }) => {
          this.$toast.error(response?.data?.message || 'Error deleting grace period');
        })
        .finally(() => {
          this.removeLoadingTablet(deviceId);
        });
    },

    deleteTablet(deviceId) {
      this.$http
        .delete(`devices/${deviceId}/tablet`)
        .then(({ data }) => {
          this.$toast.success(data.message || 'Tablet deleted successfully!');
          this.fetchData();
        })
        .catch(({ response }) => {
          this.$toast.error(response?.data?.message || 'Error deleting tablet');
        })
        .finally(() => {
          this.removeLoadingTablet(deviceId);
        });
    },

    viewHistory(id) {
      this.$refs.historyModal.open(id);
    },

    addLoadingTablet(id) {
      this.loadingTablets[id] = true;
    },

    removeLoadingTablet(id) {
      delete this.loadingTablets[id];
    },
  },
};
</script>
